import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Upload, FileText, Plus, ListChecks, Loader2, CheckCircle2,
  AlertTriangle, Download,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

/**
 * Upload Listings — single page that adapts based on the user's tier.
 *
 * - Auction houses (subscription_tier='auction_house') submit to
 *   /api/auction-listings with guide_price + auction_date.
 * - Estate agents (tier='estate_agent') submit to /api/agent-listings
 *   with asking_price, optional EPC override, status.
 * - Deal sources submit to /api/deal-listings.
 * - White-label and superusers can pick which mode (radio at top).
 *
 * EPC: leave blank to auto-match PropScope register. Enter a rating to
 * override; if it differs from the register, confirm before save.
 */

const UPLOADER_TIERS = new Set(['auction_house', 'estate_agent', 'deal_source', 'white_label', 'admin']);

const PROPERTY_TYPES = [
  'detached', 'semi-detached', 'terraced', 'end-of-terrace',
  'flat', 'maisonette', 'bungalow', 'land', 'other',
];

const EPC_OPTIONS = ['', 'A', 'B', 'C', 'D', 'E', 'F', 'G'];

const TENURE_OPTIONS = ['', 'freehold', 'leasehold'];

const AGENT_STATUSES = [
  { value: 'active',      label: 'Available' },
  { value: 'under_offer', label: 'Under Offer' },
  { value: 'sold_stc',    label: 'Sold STC' },
];

const tabs = [
  { id: 'manual',   label: 'Add a Lot',     icon: Plus },
  { id: 'bulk',     label: 'Bulk Upload',   icon: Upload },
  { id: 'listings', label: 'My Listings',   icon: ListChecks },
];

const unifiedCsvHeader =
  'listing_type,address_line1,address_line2,town,county,country,postcode,' +
  'property_type,bedrooms,bathrooms,reception_rooms,' +
  'asking_price,guide_price,auction_date,epc_rating,tenure,status,description,' +
  'sourcing_fee,gdv_estimate,refurb_cost_estimate,deal_expiry_date';

const EMPTY_FORM = {
  address_line1: '',
  address_line2: '',
  town: '',
  county: '',
  country: 'United Kingdom',
  postcode: '',
  property_type: '',
  bedrooms: '',
  bathrooms: '',
  reception_rooms: '',
  description: '',
  guide_price: '',
  auction_date: '',
  tenure: '',
  asking_price: '',
  epc_rating: '',
  status: 'active',
  sourcing_fee: '',
  gdv_estimate: '',
  refurb_cost_estimate: '',
  deal_expiry_date: '',
};


function authedFetch(url, options = {}) {
  const token = localStorage.getItem('assetlens_token');
  return fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      Authorization: `Bearer ${token}`,
    },
  });
}


function parseApiError(data, status) {
  const detail = data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    return detail;
  }
  if (typeof detail === 'string') return { message: detail };
  if (Array.isArray(detail)) {
    return { message: detail.map((d) => d.msg || JSON.stringify(d)).join('; ') };
  }
  return { message: `HTTP ${status}` };
}


function epcMismatchCopy(registerRating, enteredRating) {
  return `EPC Data says ${registerRating}, you have entered ${enteredRating}. Is this correct?`;
}


// ──────────────────────────────────────────────────────────────────────
// EPC mismatch confirm modal
// ──────────────────────────────────────────────────────────────────────

function EpcConfirmModal({ registerRating, enteredRating, onConfirm, onCancel, busy }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="epc-confirm-title"
        className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-2xl p-5 shadow-xl space-y-4"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-amber-400 shrink-0 mt-0.5" size={20} />
          <div>
            <h2 id="epc-confirm-title" className="text-white font-semibold text-base">
              Confirm EPC rating
            </h2>
            <p className="text-slate-300 text-sm mt-2">
              {epcMismatchCopy(registerRating, enteredRating)}
            </p>
            <p className="text-slate-500 text-xs mt-2">
              Leave EPC blank next time to use the PropScope register match automatically.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 text-sm rounded-xl border border-slate-600 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : null}
            Yes, use my rating
          </button>
        </div>
      </div>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────
// Manual form
// ──────────────────────────────────────────────────────────────────────

function ManualForm({ mode, onSubmitted }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [epcConfirm, setEpcConfirm] = useState(null); // { register, entered, pendingBody, url }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const buildBody = (confirmOverride = false) => {
    const isAuction = mode === 'auction_house';
    const isDeal = mode === 'deal_source';
    const base = {
      address_line1: form.address_line1.trim(),
      address_line2: form.address_line2.trim() || null,
      town: form.town.trim(),
      county: form.county.trim() || null,
      country: form.country.trim() || 'United Kingdom',
      postcode: form.postcode.trim().toUpperCase(),
      property_type: form.property_type || null,
      bedrooms: form.bedrooms ? Number(form.bedrooms) : null,
      bathrooms: form.bathrooms ? Number(form.bathrooms) : null,
      reception_rooms: form.reception_rooms ? Number(form.reception_rooms) : null,
      tenure: form.tenure || null,
      description: form.description || null,
      epc_rating: form.epc_rating || null,
      confirm_epc_override: confirmOverride,
    };

    if (isDeal) {
      return {
        ...base,
        asking_price: form.asking_price ? Number(form.asking_price) : null,
        sourcing_fee: form.sourcing_fee ? Number(form.sourcing_fee) : null,
        gdv_estimate: form.gdv_estimate ? Number(form.gdv_estimate) : null,
        refurb_cost_estimate: form.refurb_cost_estimate
          ? Number(form.refurb_cost_estimate)
          : null,
        deal_expiry_date: form.deal_expiry_date || null,
      };
    }
    if (isAuction) {
      return {
        ...base,
        guide_price: form.guide_price ? Number(form.guide_price) : null,
        auction_date: form.auction_date || null,
      };
    }
    return {
      ...base,
      asking_price: form.asking_price ? Number(form.asking_price) : null,
      status: form.status || 'active',
    };
  };

  const endpointForMode = () => {
    if (mode === 'deal_source') return '/api/deal-listings';
    if (mode === 'auction_house') return '/api/auction-listings';
    return '/api/agent-listings';
  };

  const postListing = async (body) => {
    const url = endpointForMode();
    const r = await authedFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await r.json().catch(() => ({}));
    if (r.status === 409) {
      const detail = parseApiError(data, r.status);
      if (detail.epc_mismatch) {
        return { mismatch: detail };
      }
    }
    if (!r.ok) {
      const err = parseApiError(data, r.status);
      throw new Error(err.message || `HTTP ${r.status}`);
    }
    return { ok: data };
  };

  const finishSuccess = (data) => {
    if (data.deduplicated) {
      toast(`Already in our system as #${data.id}`, { icon: 'ℹ️' });
    } else {
      const epcNote = data.epc_energy_rating ? ` · EPC ${data.epc_energy_rating}` : '';
      toast.success(`Created listing #${data.id} — ${data.address}${epcNote}`);
    }
    setForm((f) => ({
      ...EMPTY_FORM,
      property_type: f.property_type,
      bedrooms: f.bedrooms,
      bathrooms: f.bathrooms,
      reception_rooms: f.reception_rooms,
      tenure: f.tenure,
      status: f.status,
      country: f.country || 'United Kingdom',
    }));
    setEpcConfirm(null);
    if (onSubmitted) onSubmitted();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.address_line1.trim() || !form.town.trim() || !form.postcode.trim()) {
      toast.error('Address line 1, town, and postcode are required');
      return;
    }
    setSubmitting(true);
    try {
      const body = buildBody(false);
      const result = await postListing(body);
      if (result.mismatch) {
        setEpcConfirm({
          registerRating: result.mismatch.register_rating,
          enteredRating: result.mismatch.entered_rating,
        });
        return;
      }
      finishSuccess(result.ok);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmEpcOverride = async () => {
    setSubmitting(true);
    try {
      const body = buildBody(true);
      const result = await postListing(body);
      if (result.mismatch) {
        toast.error('Still blocked — try again or clear EPC to use the register.');
        return;
      }
      finishSuccess(result.ok);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const isAuction = mode === 'auction_house';
  const isDeal = mode === 'deal_source';

  return (
    <>
      {epcConfirm && (
        <EpcConfirmModal
          registerRating={epcConfirm.registerRating}
          enteredRating={epcConfirm.enteredRating}
          busy={submitting}
          onCancel={() => setEpcConfirm(null)}
          onConfirm={confirmEpcOverride}
        />
      )}

      <form onSubmit={submit} className="space-y-4 bg-slate-900 border border-slate-800 rounded-2xl p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Address line 1" required>
            <input
              type="text"
              value={form.address_line1}
              onChange={set('address_line1')}
              placeholder="123 Example Road"
              className={inputCls}
              required
            />
          </Field>
          <Field label="Address line 2">
            <input
              type="text"
              value={form.address_line2}
              onChange={set('address_line2')}
              placeholder="Flat / building (optional)"
              className={inputCls}
            />
          </Field>
          <Field label="Town / city" required>
            <input
              type="text"
              value={form.town}
              onChange={set('town')}
              placeholder="London"
              className={inputCls}
              required
            />
          </Field>
          <Field label="County">
            <input
              type="text"
              value={form.county}
              onChange={set('county')}
              placeholder="Optional"
              className={inputCls}
            />
          </Field>
          <Field label="Country">
            <input
              type="text"
              value={form.country}
              onChange={set('country')}
              className={inputCls}
            />
          </Field>
          <Field label="Postcode" required>
            <input
              type="text"
              value={form.postcode}
              onChange={set('postcode')}
              placeholder="SW1A 1AA"
              className={inputCls + ' uppercase'}
              required
            />
          </Field>
          <Field label="Property type">
            <select value={form.property_type} onChange={set('property_type')} className={inputCls}>
              <option value="">— select —</option>
              {PROPERTY_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </Field>
          <Field label="Tenure">
            <select value={form.tenure} onChange={set('tenure')} className={inputCls}>
              {TENURE_OPTIONS.map((t) => (
                <option key={t} value={t}>{t || '— select —'}</option>
              ))}
            </select>
          </Field>
          <Field label="Bedrooms">
            <input type="number" min="0" value={form.bedrooms} onChange={set('bedrooms')} className={inputCls} />
          </Field>
          <Field label="Bathrooms">
            <input type="number" min="0" value={form.bathrooms} onChange={set('bathrooms')} className={inputCls} />
          </Field>
          <Field label="Reception rooms">
            <input
              type="number"
              min="0"
              value={form.reception_rooms}
              onChange={set('reception_rooms')}
              className={inputCls}
            />
          </Field>
          <Field label="EPC rating (optional)">
            <select value={form.epc_rating} onChange={set('epc_rating')} className={inputCls}>
              {EPC_OPTIONS.map((e) => (
                <option key={e || 'blank'} value={e}>
                  {e || '— auto-match register —'}
                </option>
              ))}
            </select>
          </Field>

          {isAuction ? (
            <>
              <Field label="Guide price (£)">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.guide_price}
                  onChange={set('guide_price')}
                  className={inputCls}
                />
              </Field>
              <Field label="Auction date">
                <input
                  type="date"
                  value={form.auction_date}
                  onChange={set('auction_date')}
                  className={inputCls}
                />
              </Field>
            </>
          ) : isDeal ? (
            <>
              <Field label="Asking / deal price (£)">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.asking_price}
                  onChange={set('asking_price')}
                  className={inputCls}
                />
              </Field>
              <Field label="Sourcing fee (£)">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.sourcing_fee}
                  onChange={set('sourcing_fee')}
                  className={inputCls}
                />
              </Field>
              <Field label="GDV estimate (£)">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.gdv_estimate}
                  onChange={set('gdv_estimate')}
                  className={inputCls}
                />
              </Field>
              <Field label="Refurb cost estimate (£)">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.refurb_cost_estimate}
                  onChange={set('refurb_cost_estimate')}
                  className={inputCls}
                />
              </Field>
              <Field label="Deal expiry date">
                <input
                  type="date"
                  value={form.deal_expiry_date}
                  onChange={set('deal_expiry_date')}
                  className={inputCls}
                />
              </Field>
            </>
          ) : (
            <>
              <Field label="Asking price (£)">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.asking_price}
                  onChange={set('asking_price')}
                  className={inputCls}
                />
              </Field>
              <Field label="Status">
                <select value={form.status} onChange={set('status')} className={inputCls}>
                  {AGENT_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </Field>
            </>
          )}
        </div>

        <p className="text-xs text-slate-500">
          EPC is optional. Leave blank to use PropScope’s register match. If you enter a rating
          that differs from the register, you will be asked to confirm before save.
        </p>

        <Field label="Description">
          <textarea
            rows="3"
            value={form.description}
            onChange={set('description')}
            placeholder={
              isAuction
                ? 'Brief lot description, condition notes, vacant/tenanted, legal pack URL…'
                : 'Description for marketing — features, location highlights, etc.'
            }
            className={inputCls + ' resize-y'}
          />
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-white text-sm font-semibold rounded-xl"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {isAuction ? 'Add lot' : isDeal ? 'Add deal' : 'Add listing'}
          </button>
        </div>
      </form>
    </>
  );
}


// ──────────────────────────────────────────────────────────────────────
// CSV bulk upload
// ──────────────────────────────────────────────────────────────────────

function BulkUploadForm({ onUploaded }) {
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [mismatchesAcknowledged, setMismatchesAcknowledged] = useState(false);
  const csvHeader = unifiedCsvHeader;
  const url = '/api/unified-listings/upload-csv';

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setSubmitting(true);
    setResult(null);
    setMismatchesAcknowledged(false);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await authedFetch(url, { method: 'POST', body: fd });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const err = parseApiError(data, r.status);
        throw new Error(err.message || `HTTP ${r.status}`);
      }
      setResult(data);
      const mismatchCount = (data.epc_mismatches || []).length;
      toast.success(
        `Imported ${data.imported}, skipped ${data.duplicates} duplicates, ${data.errors} errors` +
          (mismatchCount ? ` · ${mismatchCount} EPC mismatch(es) to review` : ''),
      );
      if (onUploaded) onUploaded();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const downloadTemplate = async () => {
    let header = csvHeader;
    try {
      const r = await authedFetch('/api/unified-listings/template');
      if (r.ok) {
        const data = await r.json();
        if (data.header) header = data.header;
      }
    } catch {
      /* use local fallback */
    }
    const blob = new Blob([header + '\n'], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'listings_template.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const mismatches = result?.epc_mismatches || [];

  return (
    <form onSubmit={submit} className="bg-slate-900 border border-slate-800 rounded-2xl p-5 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-300">
          Drop a unified CSV — set <code className="text-emerald-400">listing_type</code> to
          auction, agent, or deal. Use structured address columns. Leave{' '}
          <code className="text-emerald-400">epc_rating</code> blank to auto-match the register.
        </p>
        <button
          type="button"
          onClick={downloadTemplate}
          className="inline-flex items-center gap-2 text-xs text-emerald-400 hover:text-emerald-300"
        >
          <Download size={14} /> Template CSV
        </button>
      </div>

      <div className="border-2 border-dashed border-slate-700 rounded-xl p-6 text-center hover:border-emerald-500 transition-colors">
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="block mx-auto text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-800 file:text-slate-200 file:hover:bg-slate-700 file:cursor-pointer"
        />
        {file && (
          <p className="mt-3 text-xs text-emerald-400">
            <FileText size={12} className="inline mr-1" /> {file.name} ({Math.round(file.size / 1024)} KB)
          </p>
        )}
      </div>

      <div className="text-xs text-slate-500 font-mono bg-slate-950 border border-slate-800 rounded-lg p-3 overflow-x-auto whitespace-pre">
        {csvHeader}
      </div>

      <p className="text-xs text-slate-500">
        Optional <code className="text-slate-400">epc_rating</code>: your value is stored. If it
        differs from PropScope’s EPC register, those rows appear below for you to acknowledge.
        Legacy single <code className="text-slate-400">address</code> column is still accepted.
      </p>

      {result && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center text-sm">
          <Stat label="Imported" value={result.imported} colour="text-emerald-400" />
          <Stat label="Duplicates" value={result.duplicates} colour="text-amber-400" />
          <Stat label="Errors" value={result.errors} colour="text-rose-400" />
          <Stat label="EPC auto" value={result.epc_auto_matched ?? 0} colour="text-sky-400" />
          <Stat label="EPC override" value={result.epc_overrides ?? 0} colour="text-violet-400" />
        </div>
      )}

      {mismatches.length > 0 && (
        <div className="border border-amber-500/40 bg-amber-500/5 rounded-xl p-4 space-y-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="text-amber-400 shrink-0 mt-0.5" size={16} />
            <div>
              <p className="text-sm text-amber-200 font-medium">
                {mismatches.length} EPC mismatch{mismatches.length === 1 ? '' : 'es'} — please confirm
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Your entered ratings were saved. Acknowledge that these overrides are intentional.
              </p>
            </div>
          </div>
          <ul className="space-y-2 max-h-64 overflow-y-auto">
            {mismatches.map((m) => (
              <li
                key={`${m.row}-${m.property_id}`}
                className="text-xs bg-slate-950/60 border border-slate-800 rounded-lg px-3 py-2 text-slate-300"
              >
                <span className="text-slate-500">Row {m.row}</span>
                {' · '}
                <span className="text-white">{m.address}</span>
                {m.postcode ? ` (${m.postcode})` : ''}
                <div className="mt-1 text-amber-200/90">
                  {m.message || epcMismatchCopy(m.register_rating, m.entered_rating)}
                </div>
              </li>
            ))}
          </ul>
          {!mismatchesAcknowledged ? (
            <button
              type="button"
              onClick={() => {
                setMismatchesAcknowledged(true);
                toast.success('EPC overrides acknowledged');
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-100 text-sm font-medium rounded-xl"
            >
              <CheckCircle2 size={14} />
              Confirm overrides ({mismatches.length})
            </button>
          ) : (
            <p className="text-xs text-emerald-400 inline-flex items-center gap-1.5">
              <CheckCircle2 size={14} /> Overrides acknowledged
            </p>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={!file || submitting}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-white text-sm font-semibold rounded-xl"
        >
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          Upload
        </button>
      </div>
    </form>
  );
}


// ──────────────────────────────────────────────────────────────────────
// My Listings table
// ──────────────────────────────────────────────────────────────────────

function MyListings({ mode, refreshKey }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const url =
    mode === 'auction_house'
      ? '/api/auction-listings'
      : mode === 'deal_source'
        ? '/api/deal-listings'
        : '/api/agent-listings';

  const load = useCallback(() => {
    authedFetch(url)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => {
        setRows(Array.isArray(d) ? d : d.items || []);
        setError(null);
      })
      .catch((e) => setError(e.message));
  }, [url]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-300 text-sm">
        <AlertTriangle size={16} /> {error}
      </div>
    );
  }
  if (!rows) {
    return (
      <div className="text-slate-400 text-sm flex items-center gap-2">
        <Loader2 size={16} className="animate-spin" /> Loading…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <p className="text-slate-500 text-sm">
        No listings uploaded yet. Use the “Add a Lot” or “Bulk Upload” tab to start.
      </p>
    );
  }

  return (
    <div className="overflow-hidden bg-slate-900 border border-slate-800 rounded-2xl">
      <table className="w-full text-sm">
        <thead className="bg-slate-950/50 text-slate-400 text-xs uppercase tracking-wide">
          <tr>
            <th className="text-left px-4 py-3 font-medium">Address</th>
            <th className="text-left px-4 py-3 font-medium">Postcode</th>
            <th className="text-right px-4 py-3 font-medium">Price</th>
            {mode === 'auction_house' && (
              <th className="text-left px-4 py-3 font-medium">Auction date</th>
            )}
            <th className="text-left px-4 py-3 font-medium">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-slate-800/40">
              <td className="px-4 py-3 text-white">{r.address}</td>
              <td className="px-4 py-3 text-slate-400 font-mono text-xs">{r.postcode}</td>
              <td className="px-4 py-3 text-right text-emerald-400 tabular-nums">
                {r.asking_price ? `£${Number(r.asking_price).toLocaleString()}` : '—'}
              </td>
              {mode === 'auction_house' && (
                <td className="px-4 py-3 text-slate-400 text-xs">
                  {r.auction_date ? new Date(r.auction_date).toLocaleDateString('en-GB') : '—'}
                </td>
              )}
              <td className="px-4 py-3 text-slate-400 capitalize">
                {r.status?.replace('_', ' ') || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────
// Main page
// ──────────────────────────────────────────────────────────────────────

export default function UploadListings() {
  const { user } = useAuth();
  const [tab, setTab] = useState('manual');
  const [refreshKey, setRefreshKey] = useState(0);

  const initialMode = useMemo(() => {
    if (user?.subscription_tier === 'auction_house') return 'auction_house';
    if (user?.subscription_tier === 'estate_agent') return 'estate_agent';
    if (user?.subscription_tier === 'deal_source') return 'deal_source';
    return 'auction_house';
  }, [user]);

  const [mode, setMode] = useState(initialMode);
  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  if (user === null) return null;
  const allowed = user?.is_superuser || UPLOADER_TIERS.has(user?.subscription_tier);
  if (!allowed) return <Navigate to="/dashboard" replace />;

  const canSwitch = user?.is_superuser || user?.subscription_tier === 'white_label';

  return (
    <div className="p-6 space-y-5 max-w-5xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Upload size={22} className="text-emerald-400" /> Upload Listings
        </h1>
        <p className="text-slate-400 text-sm mt-1">
          {mode === 'auction_house'
            ? 'Add auction lots manually or bulk-upload via CSV. Listings are deduplicated against existing stock and enriched automatically.'
            : mode === 'deal_source'
              ? 'Add sourced deals manually or bulk-upload via CSV. Listings are deduplicated and enriched automatically.'
              : 'Add property listings manually or bulk-upload via CSV. Listings are deduplicated and enriched automatically.'}
        </p>
      </div>

      {canSwitch && (
        <div className="flex items-center gap-3 text-sm">
          <span className="text-slate-400">Submit as:</span>
          {[
            { value: 'auction_house', label: 'Auction lot' },
            { value: 'estate_agent', label: 'Agent listing' },
            { value: 'deal_source', label: 'Deal' },
          ].map((opt) => (
            <label
              key={opt.value}
              className={`px-3 py-1.5 rounded-lg cursor-pointer border ${
                mode === opt.value
                  ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300'
                  : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
              }`}
            >
              <input
                type="radio"
                name="mode"
                value={opt.value}
                checked={mode === opt.value}
                onChange={() => setMode(opt.value)}
                className="hidden"
              />
              {opt.label}
            </label>
          ))}
        </div>
      )}

      <div className="flex gap-2 border-b border-slate-800">
        {tabs.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? 'border-emerald-500 text-emerald-300'
                  : 'border-transparent text-slate-400 hover:text-slate-200'
              }`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'manual' && (
        <ManualForm mode={mode} onSubmitted={() => setRefreshKey((k) => k + 1)} />
      )}
      {tab === 'bulk' && (
        <BulkUploadForm onUploaded={() => setRefreshKey((k) => k + 1)} />
      )}
      {tab === 'listings' && <MyListings mode={mode} refreshKey={refreshKey} />}
    </div>
  );
}


const inputCls =
  'w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:border-emerald-500 outline-none';

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wide text-slate-400 font-semibold mb-1">
        {label}
        {required && <span className="text-rose-400 ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}

function Stat({ label, value, colour }) {
  return (
    <div className="bg-slate-800 rounded-lg p-3">
      <div className={`text-2xl font-bold ${colour}`}>{value}</div>
      <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
    </div>
  );
}
